import type { EnhanceSource, Workflow } from '../types'

export type EnhanceInterpolator = 'rife' | 'gimm'

export const DEFAULT_ENHANCE_SOURCE_FPS = 16
export const DEFAULT_GIMM_MODEL = 'gimmvfi_r_arb_lpips_fp32.safetensors'

export interface EnhanceWorkflowOptions {
  filename: string
  kind: 'image' | 'video'
  upscaleModel: string
  scale: number
  targetFps: number
  rifeModel: string
  preserveAudio: boolean
  sourceFps?: number
  interpolator?: EnhanceInterpolator
  gimmModel?: string
  loop?: boolean
  /** Exact output size; replaces the scale factor for the final resize. */
  targetSize?: { width: number; height: number }
}

export const ENHANCE_LONG_EDGE_MIN = 256
export const ENHANCE_LONG_EDGE_MAX = 4096

// yuv420p video needs even dimensions.
const even = (value: number): number => Math.max(2, Math.round(value / 2) * 2)

const DEFAULT_LONG_EDGE = 1920

/** One rule for the field (on blur), the stored draft and the submitted size. */
export function normalizeLongEdge(value: unknown): number {
  const number = Number(value)
  if (!Number.isFinite(number)) return DEFAULT_LONG_EDGE
  return even(Math.max(ENHANCE_LONG_EDGE_MIN, Math.min(ENHANCE_LONG_EDGE_MAX, number)))
}

// ImageScaleBy sizes with Python's round(), which rounds halves to the nearest even integer.
const pythonRound = (value: number): number => {
  const floor = Math.floor(value)
  if (value - floor !== 0.5) return Math.round(value)
  return floor % 2 === 0 ? floor : floor + 1
}

export function enhanceOutputSize(
  source: { width: number; height: number } | null,
  settings: { scaleMode: 'factor' | 'longEdge'; scale: number; longEdge: number; upscaleModel?: string },
): { width: number; height: number; factor: number } | null {
  if (!source || !(source.width > 0) || !(source.height > 0)) return null
  if (settings.scaleMode === 'factor') {
    // Mirrors the workflow: the model upscales by its native scale, then ImageScaleBy applies scale / native.
    const native = nativeScale(settings.upscaleModel ?? '')
    const scaleBy = Math.max(0.01, settings.scale / native)
    return {
      width: pythonRound(source.width * native * scaleBy),
      height: pythonRound(source.height * native * scaleBy),
      factor: settings.scale,
    }
  }
  const longEdge = normalizeLongEdge(settings.longEdge)
  const factor = longEdge / Math.max(source.width, source.height)
  return source.width >= source.height
    ? { width: longEdge, height: even(source.height * factor), factor }
    : { width: even(source.width * factor), height: longEdge, factor }
}

/** Runs `task` unless one started with the same lock is still pending (e.g. a double click during an await). */
export async function runExclusive(lock: { current: boolean }, task: () => Promise<void>): Promise<void> {
  if (lock.current) return
  lock.current = true
  try {
    await task()
  } finally {
    lock.current = false
  }
}

export function enhanceMultiplier(targetFps: number, sourceFps: number = DEFAULT_ENHANCE_SOURCE_FPS): number {
  const base = sourceFps > 0 ? sourceFps : DEFAULT_ENHANCE_SOURCE_FPS
  return Math.max(1, Math.round(Math.max(1, Math.round(targetFps)) / base))
}

export const ENHANCE_SOURCE_PRESETS: Record<EnhanceSource, { sourceFps: number; interpolator: EnhanceInterpolator }> = {
  wan: { sourceFps: 16, interpolator: 'rife' },
  h3: { sourceFps: 24, interpolator: 'gimm' },
}

export function enhanceSourceForFps(fps: number | null): EnhanceSource | null {
  if (!fps) return null
  if (Math.abs(fps - ENHANCE_SOURCE_PRESETS.h3.sourceFps) < 0.5) return 'h3'
  if (Math.abs(fps - ENHANCE_SOURCE_PRESETS.wan.sourceFps) < 0.5) return 'wan'
  return null
}

export function resolveEnhanceSource(state: {
  manual: boolean
  info: { fps: number } | null
  selected: EnhanceSource
}): EnhanceSource {
  return state.manual ? state.selected : enhanceSourceForFps(state.info?.fps ?? null) ?? state.selected
}

type EnhanceVideoInfo = { fps: number; frames: number }

/**
 * Call synchronously at the Generate click: `manual` and `selected` are captured before the first await,
 * so later edits or a file swap cannot leak into the job being submitted.
 */
export async function resolveEnhanceSubmission(
  probe: { info: Promise<EnhanceVideoInfo | null>; manual: boolean } | null,
  selected: EnhanceSource,
  inspect: () => Promise<EnhanceVideoInfo | null>,
): Promise<{ info: EnhanceVideoInfo | null; enhanceSource: EnhanceSource }> {
  const manual = probe?.manual ?? false
  const info = await (probe ? probe.info : inspect())
  return { info, enhanceSource: resolveEnhanceSource({ manual, info, selected }) }
}

// ImageFromBatch clamps an out-of-range index to the final frame, but its index tops out at 4095.
const LOOP_LAST_FRAME_INDEX = 4095

/** VHS_LoadVideo resamples the clip to force_rate, so the loaded batch can differ from the file's frame count. */
export function enhanceLoopFrameLimitExceeded(info: { fps: number; frames: number } | null, forceRate: number): boolean {
  if (!info || !(info.fps > 0)) return false
  return Math.ceil(info.frames * forceRate / info.fps) > LOOP_LAST_FRAME_INDEX + 1
}

export const ENHANCE_RIFE_MODEL = 'rife49'

/** Servers list the checkpoint with or without its extension (rife49 / rife49.pth). */
export function selectRifeModel(models: string[]): string | null {
  return models.find((model) => model.replace(/\.(pth|pkl)$/i, '') === ENHANCE_RIFE_MODEL) ?? null
}

export function selectGimmModel(models: string[]): string {
  return models.find((model) => model === DEFAULT_GIMM_MODEL) ?? models[0] ?? DEFAULT_GIMM_MODEL
}

export function enhanceInterpolationNodes(interpolator: EnhanceInterpolator): string[] {
  return interpolator === 'gimm' ? ['DownloadAndLoadGIMMVFIModel', 'GIMMVFI_interpolate'] : ['RIFE VFI']
}

export type ConcatBackend = 'mugen' | 'legacy'

export interface ConcatWorkflowOptions {
  filenames: string[]
  fps: number
  crf: number
  outputName?: string
  backend?: ConcatBackend
}

const MUGEN_CONCAT_NODES = ['MugenVideoPathLoader', 'MugenFFmpegConcat'] as const
const LEGACY_CONCAT_NODES = ['VHS_LoadVideoFFmpeg', 'FFmpegVideoMerge_Pro'] as const

export function selectConcatBackend(nodes: string[]): ConcatBackend | null {
  const available = new Set(nodes)
  if (MUGEN_CONCAT_NODES.every((nodeType) => available.has(nodeType))) return 'mugen'
  if (LEGACY_CONCAT_NODES.every((nodeType) => available.has(nodeType))) return 'legacy'
  return null
}

const node = (classType: string, title: string, inputs: Record<string, unknown>) => ({
  class_type: classType,
  _meta: { title },
  inputs,
})

const nativeScale = (name: string): number => {
  const prefix = name.match(/(?:^|[^0-9])(\d+(?:\.\d+)?)x(?:[^a-z]|$)/i)
  const suffix = name.match(/(?:^|[^a-z])x(\d+(?:\.\d+)?)(?:[^0-9]|$)/i)
  return Math.max(1, Number(prefix?.[1] ?? suffix?.[1] ?? 1))
}

export function buildEnhanceWorkflow(options: EnhanceWorkflowOptions): Workflow {
  const scaleBy = Math.max(0.01, options.scale / nativeScale(options.upscaleModel))
  const targetFps = Math.max(1, Math.round(options.targetFps))
  const multiplier = enhanceMultiplier(targetFps, options.sourceFps)
  // GIMM-VFI runs on the source-resolution frames; upscaling them first would quadruple its VRAM use.
  const gimmFirst = options.kind === 'video' && options.interpolator === 'gimm' && multiplier > 1
  const workflow: Workflow = {
    '1': node(options.kind === 'image' ? 'LoadImage' : 'VHS_LoadVideo', 'Load Source', options.kind === 'image'
      ? { image: options.filename }
      : {
          video: options.filename,
          force_rate: targetFps / multiplier,
          custom_width: 0,
          custom_height: 0,
          frame_load_cap: 0,
          skip_first_frames: 0,
          select_every_nth: 1,
        }),
    '2': node('UpscaleModelLoader', 'Load Upscale Model', { model_name: options.upscaleModel }),
    '3': node('ImageUpscaleWithModel', 'Model Upscale', { upscale_model: ['2', 0], image: [gimmFirst ? '5' : '1', 0] }),
    '4': options.targetSize
      ? node('ImageScale', 'Final Size', {
          image: ['3', 0], upscale_method: 'lanczos', width: options.targetSize.width, height: options.targetSize.height, crop: 'disabled',
        })
      : node('ImageScaleBy', 'Final Scale', { image: ['3', 0], upscale_method: 'lanczos', scale_by: scaleBy }),
  }

  if (options.kind === 'image') {
    workflow['5'] = node('SaveImage', 'Save Enhanced Image', { filename_prefix: 'Mugen2_Enhanced', images: ['4', 0] })
    return workflow
  }

  const interpolationSource: [string, number] = [gimmFirst ? '1' : '4', 0]
  const closeLoop = (interpolationInputs: Record<string, unknown>, framesInput: string): [string, number] => {
    workflow['8'] = node('ImageFromBatch', 'Loop: Last Frame', { image: interpolationSource, batch_index: LOOP_LAST_FRAME_INDEX, length: 1 })
    workflow['9'] = node('ImageFromBatch', 'Loop: First Frame', { image: interpolationSource, batch_index: 0, length: 1 })
    workflow['10'] = node('ImageBatch', 'Loop: Last + First', { image1: ['8', 0], image2: ['9', 0] })
    workflow['11'] = node(workflow['5'].class_type, 'Loop: Interpolation', { ...interpolationInputs, [framesInput]: ['10', 0] })
    workflow['12'] = node('ImageFromBatch', 'Loop: In-between Frames', { image: ['11', 0], batch_index: 1, length: multiplier - 1 })
    workflow['13'] = node('ImageBatch', 'Frames + Loop Closure', { image1: ['5', 0], image2: ['12', 0] })
    return ['13', 0]
  }

  let frames: [string, number] = ['4', 0]
  if (gimmFirst) {
    workflow['7'] = node('DownloadAndLoadGIMMVFIModel', 'Load GIMM-VFI Model', {
      model: options.gimmModel || DEFAULT_GIMM_MODEL,
      precision: 'fp32',
      torch_compile: false,
    })
    workflow['5'] = node('GIMMVFI_interpolate', 'GIMM-VFI Frame Interpolation', {
      gimmvfi_model: ['7', 0],
      images: ['1', 0],
      ds_factor: 1,
      interpolation_factor: multiplier,
      seed: 0,
      output_flows: false,
    })
    if (options.loop) workflow['3'].inputs.image = closeLoop(workflow['5'].inputs, 'images')
  } else if (multiplier > 1) {
    workflow['5'] = node('RIFE VFI', 'RIFE Frame Interpolation', {
      ckpt_name: options.rifeModel,
      frames: ['4', 0],
      clear_cache_after_n_frames: 10,
      multiplier,
      fast_mode: true,
      ensemble: true,
      scale_factor: 1,
    })
    frames = options.loop ? closeLoop(workflow['5'].inputs, 'frames') : ['5', 0]
  }
  workflow['6'] = node('VHS_VideoCombine', 'Save Enhanced Video', {
    images: frames,
    frame_rate: targetFps,
    loop_count: 0,
    filename_prefix: 'Mugen2_Enhanced',
    format: 'video/h264-mp4',
    pix_fmt: 'yuv420p',
    crf: 14, // USER 2026-10-12：放大補幀成品是 AE 後製的素材（下游再壓兩次）・自 18 降 14,
    save_metadata: false,
    trim_to_audio: false,
    pingpong: false,
    save_output: true,
    ...(options.preserveAudio ? { audio: ['1', 2] } : {}),
  })
  return workflow
}

export function buildConcatWorkflow(options: ConcatWorkflowOptions): Workflow {
  if (options.filenames.length < 2 || options.filenames.length > 8) {
    throw new Error('Concat は 2～8 本の動画が必要です。')
  }
  const backend = options.backend ?? 'mugen'
  const workflow: Workflow = {}
  options.filenames.forEach((filename, index) => {
    workflow[String(index + 1)] = backend === 'mugen'
      ? node('MugenVideoPathLoader', `Load Video ${index + 1}`, { video: filename })
      : node('VHS_LoadVideoFFmpeg', `Load Video ${index + 1}`, {
          video: filename,
          force_rate: 0,
          custom_width: 0,
          custom_height: 0,
          frame_load_cap: 0,
          start_time: 0,
        })
  })
  workflow['20'] = node(backend === 'mugen' ? 'MugenFFmpegConcat' : 'FFmpegVideoMerge_Pro', 'Merge Videos', {
    fps: Math.max(1, Math.round(options.fps)),
    quality_crf: Math.max(0, Math.min(51, Math.round(options.crf))),
    output_name: options.outputName || `Mugen2_Concat_${Date.now()}`,
    save_output: true,
    ...Object.fromEntries(options.filenames.map((_, index) => [
      `video_${index + 1}`,
      [String(index + 1), backend === 'mugen' ? 0 : 4],
    ])),
  })
  return workflow
}
